import React from 'react';
import { App } from 'antd';
import { useGlobalState } from '../../state/useGlobalState';
import { useActions } from '../../hooks/useStore';
import { api } from '../../services/api';
import { logger } from '../../services/logger.client';
import type { SessionState } from '../../types/types';
import DataManagementPanel from './DataManagementPanel';

/**
 * Container for DataManagementPanel (ADR-034).
 *
 * Owns all store access and API calls:
 * - Export: serializes the full store state to a downloadable JSON file
 * - Import: reads a JSON file and merges it into the store via `loadStateFromObject`
 * - Reset: calls POST /api/reset-database via the `api` wrapper and reloads the page
 *
 * The confirmation dialogs are triggered here (via Ant Design's `modal.confirm`)
 * because they gate side-effectful operations that the container owns.
 */
const DataManagementPanelContainer: React.FC = () => {
  const actions = useActions();
  const { message, modal } = App.useApp();

  const handleExport = () => {
    const state = useGlobalState.getState();
    if (!state) {
      message.warning('No data available to export.');
      return;
    }
    const blob = new Blob([JSON.stringify(state)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dm-dashboard-backup.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    logger.info('Data exported successfully.');
  };

  /**
   * Validates and imports parsed JSON data into the store.
   * The confirmation dialog is shown here because it gates a store mutation.
   */
  const handleImportData = (parsedData: Record<string, unknown>): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!parsedData || typeof parsedData !== 'object') {
        const errorMsg = 'Invalid file format.';
        logger.error({ fileContent: parsedData }, 'Invalid file format during import.');
        reject(new Error(errorMsg));
        return;
      }

      modal.confirm({
        title: 'Import Data?',
        content: 'This will overwrite all current data. This action cannot be undone. Are you sure?',
        onOk: async () => {
          await actions.loadStateFromObject(parsedData as Partial<SessionState>);
          logger.info('Data imported successfully.');
          message.success('Data imported successfully!');
          resolve();
        },
        onCancel: () => {
          resolve(); // User cancelled -- not an error
        },
      });
    });
  };

  const handleReset = () => {
    modal.confirm({
      title: 'Reset All Data?',
      content: 'Are you absolutely sure you want to reset all data? This action cannot be undone.',
      onOk: async () => {
        try {
          const response = await api('/api/reset-database', { method: 'POST' });
          if (!response.ok) {
            throw new Error('Server responded with an error.');
          }
        } catch (error) {
          logger.error(error, 'Failed to reset database via API.');
          message.error('Failed to reset database. Check server logs.');
          return;
        }
        logger.warn('All application data has been reset.');
        message.success('Dashboard has been reset. Reloading...');
        setTimeout(() => location.reload(), 1500);
      },
    });
  };

  return (
    <DataManagementPanel
      onExport={handleExport}
      onImportData={handleImportData}
      onReset={handleReset}
    />
  );
};

export default DataManagementPanelContainer;
